import {
  FINANCE_CHECK_SECOND_ORDER_TYPE_LABELS,
  FINANCE_CHECK_STATUS_LABELS,
  FINANCE_IN_OUT_LABELS,
  FINANCE_PAYMENT_METHOD_LABELS,
  FINANCE_REPORT_STATUS_LABELS,
  FINANCE_SETTLEMENT_STATUS_LABELS,
} from '@/temu-platform/constants/temuApiCatalog';
import { pickField } from '@/temu-platform/utils/temuResponse';
import {
  FINANCE_MODE_CHECK,
  FINANCE_MODE_REPORT,
  temuShopCapabilities,
} from '@/temu-platform/utils/temuShopCapabilities';
import i18n from '@/i18n';

/** 收支明细（平台对账单 get-check-order-*） */
export const BILL_TYPE_CHECK = 'CHECK';
/** 结算账单（平台报账单 report-*） */
export const BILL_TYPE_REPORT = 'REPORT';

export const BILL_TYPE_TABS = [
  { id: BILL_TYPE_CHECK, label: '收支明细', labelKey: 'finance.tabCheck' },
  { id: BILL_TYPE_REPORT, label: '结算账单', labelKey: 'finance.tabReport' },
];

/** 收支明细打款维度 */
export const CHECK_PAY_UNPAID = 'UNPAID';
export const CHECK_PAY_PAID = 'PAID';

export const CHECK_PAY_TABS = [
  { id: CHECK_PAY_UNPAID, label: '未打款', labelKey: 'finance.payUnpaid' },
  { id: CHECK_PAY_PAID, label: '已打款', labelKey: 'finance.payPaid' },
];

function financeText(key, fallback) {
  return i18n.t(key, { ns: 'workbench', defaultValue: fallback });
}

export function checkPayTimeLabel(payStatus) {
  return payStatus === CHECK_PAY_PAID
    ? i18n.t('finance.settled', { ns: 'workbench' })
    : i18n.t('finance.estimatePay', { ns: 'workbench' });
}

export const FINANCE_IN_OUT_OPTIONS = [
  { value: '1', label: '收入' },
  { value: '2', label: '支出' },
];

export const FINANCE_SECOND_ORDER_TYPE_OPTIONS = Object.entries(FINANCE_CHECK_SECOND_ORDER_TYPE_LABELS)
  .map(([value, label]) => ({ value: String(value), label }))
  .sort((a, b) => Number(a.value) - Number(b.value));

/** 收支明细日期筛选类型（对齐售后 TemuDateRangeFilter） */
export const FINANCE_CHECK_PAY_TIME_OPTIONS = [
  { value: 'estimate', label: '预计打款' },
  { value: 'completed', label: '结算完成' },
];

/** 结算账单日期筛选类型 */
export const FINANCE_REPORT_PAY_TIME_OPTIONS = [
  { value: 'completed', label: '打款' },
];

export function defaultFinancePayTimeType(isReport, checkPayStatus) {
  if (isReport) return 'completed';
  return checkPayStatus === CHECK_PAY_PAID ? 'completed' : 'estimate';
}

export function financePayTimeField(timeType) {
  return timeType === 'completed' ? 'completedPayTime' : 'estimatePayTime';
}
/**
 * 财务 OpenAPI 按店铺模式分流（见 temu-finance.md）：
 * - 半托管/自运营：对账单 + report-order-list
 * - 全托管/POP：report-list + 销售/补扣明细
 */
export const FINANCE_API_MODE_SEMI = 'SEMI_SELF';
export const FINANCE_API_MODE_FULL = 'FULL_POP';
export const FINANCE_API_MODE_UNKNOWN = 'UNKNOWN';

export function resolveFinanceApiMode(shopType) {
  const raw = String(shopType || '').trim();
  if (!raw) return FINANCE_API_MODE_UNKNOWN;
  const caps = temuShopCapabilities(raw);
  if (caps.financeMode === FINANCE_MODE_CHECK) return FINANCE_API_MODE_SEMI;
  if (caps.financeMode === FINANCE_MODE_REPORT) return FINANCE_API_MODE_FULL;
  const upper = raw.toUpperCase();
  if (
    raw.includes('半托')
    || raw.includes('自运营')
    || upper === 'SEMI'
    || upper === 'SEMI_HOSTED'
    || upper === 'BAN_TUO'
    || upper === 'SELF'
  ) {
    return FINANCE_API_MODE_SEMI;
  }
  if (
    raw.includes('全托')
    || upper === 'POP'
    || upper === 'FULL'
    || upper === 'FULL_HOSTED'
    || upper === 'OBM'
    || upper === 'QUAN_TUO'
  ) {
    return FINANCE_API_MODE_FULL;
  }
  return FINANCE_API_MODE_UNKNOWN;
}

export function financeTabsForMode(apiMode) {
  if (apiMode === FINANCE_API_MODE_FULL) {
    return BILL_TYPE_TABS.filter((t) => t.id === BILL_TYPE_REPORT);
  }
  // 半托管/自运营：收支明细 + 结算账单；未知时先展示两 Tab，同步时由后端拦截
  return BILL_TYPE_TABS;
}

export function defaultBillTypeForMode(apiMode) {
  return apiMode === FINANCE_API_MODE_FULL ? BILL_TYPE_REPORT : BILL_TYPE_CHECK;
}

export function financeApiModeLabel(apiMode) {
  if (apiMode === FINANCE_API_MODE_SEMI) return '半托管/自运营';
  if (apiMode === FINANCE_API_MODE_FULL) return '全托管/POP';
  return '未知店铺类型';
}

export function financeTabLabel(billType) {
  return billType === BILL_TYPE_REPORT
    ? i18n.t('finance.tabReport', { ns: 'workbench' })
    : i18n.t('finance.tabCheck', { ns: 'workbench' });
}

export function financeStatusLabel(billType, raw) {
  if (raw == null || raw === '') return '—';
  const text = String(raw).trim();
  if (!/^\d+$/.test(text)) return text;
  const code = Number(text);
  if (billType === BILL_TYPE_REPORT) {
    if (FINANCE_REPORT_STATUS_LABELS[code]) {
      return financeText(`finance.stReport${code}`, FINANCE_REPORT_STATUS_LABELS[code]);
    }
    if (FINANCE_SETTLEMENT_STATUS_LABELS[code]) {
      return financeText(`finance.stSettle${code}`, FINANCE_SETTLEMENT_STATUS_LABELS[code]);
    }
    return text;
  }
  if (FINANCE_CHECK_STATUS_LABELS[code]) {
    return financeText(`finance.stCheck${code}`, FINANCE_CHECK_STATUS_LABELS[code]);
  }
  return text;
}

export function financeInOutLabel(raw) {
  if (raw == null || raw === '') return '—';
  const code = Number(raw);
  if (Number.isFinite(code) && FINANCE_IN_OUT_LABELS[code]) {
    return financeText(`finance.in${code}`, FINANCE_IN_OUT_LABELS[code]);
  }
  return String(raw);
}

export function financeSecondOrderTypeLabel(raw) {
  if (raw == null || raw === '') return '—';
  const text = String(raw).trim();
  if (!/^\d+$/.test(text)) return text;
  const code = Number(text);
  const label = FINANCE_CHECK_SECOND_ORDER_TYPE_LABELS[code]
    || FINANCE_CHECK_SECOND_ORDER_TYPE_LABELS[text];
  return label ? financeText(`finance.ot${code}`, label) : text;
}

export function financePaymentMethodLabel(raw) {
  if (raw == null || raw === '') return '—';
  const code = Number(raw);
  if (Number.isFinite(code) && FINANCE_PAYMENT_METHOD_LABELS[code]) {
    return financeText(`finance.pay${code}`, FINANCE_PAYMENT_METHOD_LABELS[code]);
  }
  return String(raw);
}

export function financeStatusModifier(raw) {
  const text = String(raw || '');
  const asNum = Number(text);
  if (asNum === 4 || /异常|失败|拒绝|取消|关闭|fail|reject|cancel/i.test(text)) {
    return 'failed';
  }
  if (asNum === 3 || /已付款|已结算|完成|成功|paid|settled/i.test(text)) {
    return 'success';
  }
  return 'pending';
}

export function billPrimaryNo(row, billType) {
  if (billType === BILL_TYPE_REPORT) {
    return pickField(row, 'reportOrderNo', 'checkOrderNo', 'billNo', 'id');
  }
  return pickField(row, 'checkOrderNo', 'billNo', 'orderNo', 'id');
}

export function formatMoney(amount, currency) {
  if (amount == null || amount === '') return '—';
  const num = Number(amount);
  const money = Number.isFinite(num)
    ? num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : String(amount);
  return currency ? `${currency} ${money}` : money;
}

/** 收支明细金额：支出显示为负值并标红，收入标绿 */
export function formatLedgerAmount(amount, currency, incomeExpenditureType) {
  if (amount == null || amount === '') return { text: '—', tone: 'neutral' };
  const num = Number(amount);
  if (!Number.isFinite(num)) {
    return { text: formatMoney(amount, currency), tone: 'neutral' };
  }
  const isExpense = Number(incomeExpenditureType) === 2 || num < 0;
  const abs = Math.abs(num);
  const body = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const prefixed = currency ? `${currency} ${body}` : body;
  if (isExpense) {
    return { text: `-${prefixed}`, tone: 'expense' };
  }
  return { text: prefixed, tone: 'income' };
}

export function formatPayDate(raw) {
  if (raw == null || raw === '') return '—';
  const text = String(raw).trim();
  // 平台返回精确到时分秒时完整展示；仅有日期则仍显示 YYYY/MM/DD
  const m = text.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (m) {
    const date = `${m[1]}/${m[2]}/${m[3]}`;
    if (m[4] != null && m[5] != null) {
      const sec = m[6] != null ? m[6] : '00';
      return `${date} ${m[4]}:${m[5]}:${sec}`;
    }
    return date;
  }
  return text;
}

export function displayFinanceValue(value) {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

export function extractDetailPayload(response) {
  if (!response || typeof response !== 'object') return null;
  if (response.detail && typeof response.detail === 'object') return response.detail;
  const info = response.info ?? response.data ?? response;
  if (info && typeof info === 'object' && !Array.isArray(info)) {
    return info;
  }
  return response;
}

export function extractDetailLines(node, keys) {
  if (!node || typeof node !== 'object') return [];
  for (const key of keys) {
    if (Array.isArray(node[key])) return node[key];
  }
  if (node.info) return extractDetailLines(node.info, keys);
  if (node.detail) return extractDetailLines(node.detail, keys);
  return [];
}

export function hasFinanceRichDetail(response) {
  if (response?.hasDetail === true) return true;
  if (Array.isArray(response?.checkOrders) && response.checkOrders.length) return true;
  const payload = extractDetailPayload(response);
  if (!payload || typeof payload !== 'object') return false;
  if (Array.isArray(payload.checkOrders) && payload.checkOrders.length) return true;
  if (Array.isArray(payload.itemList) && payload.itemList.length) return true;
  if (Array.isArray(payload.sales?.lines) && payload.sales.lines.length) return true;
  if (Array.isArray(payload.adjustments?.lines) && payload.adjustments.lines.length) return true;
  if (Array.isArray(payload.reportSalesDetails) && payload.reportSalesDetails.length) return true;
  if (Array.isArray(payload.reportReplenishDetail) && payload.reportReplenishDetail.length) return true;
  return Boolean(pickField(payload, 'checkOrderNo', 'reportOrderNo', 'bzOrderNo', 'itemList'));
}

/** 报账单详情中的关联对账单（订单收支明细） */
export function extractRelatedCheckOrders(response, detail) {
  if (Array.isArray(response?.checkOrders) && response.checkOrders.length) {
    return response.checkOrders;
  }
  if (Array.isArray(detail?.checkOrders) && detail.checkOrders.length) {
    return detail.checkOrders;
  }
  return [];
}
